"use client";

import { forwardRef } from "react";

type Variant = "primary" | "secondary" | "ghost" | "danger" | "outline";
type Size = "sm" | "md" | "lg";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
}

const variantClasses: Record<Variant, string> = {
  primary:
    "bg-accent text-white hover:bg-accent/90 disabled:bg-surface-2 disabled:text-muted disabled:hover:bg-surface-2",
  secondary:
    "bg-surface-2 text-foreground border border-border hover:bg-surface disabled:bg-surface-2 disabled:text-muted disabled:hover:bg-surface-2",
  ghost:
    "bg-transparent text-muted hover:text-foreground hover:bg-white/5 disabled:text-muted/50 disabled:hover:bg-transparent disabled:hover:text-muted/50",
  danger:
    "bg-danger text-white hover:bg-danger/90 disabled:bg-surface-2 disabled:text-muted disabled:hover:bg-surface-2",
  outline:
    "bg-transparent border border-border text-foreground hover:bg-surface-2 disabled:bg-surface-2 disabled:text-muted disabled:hover:bg-surface-2",
};

const sizeClasses: Record<Size, string> = {
  sm: "px-2.5 py-1.5 text-xs",
  md: "px-4 py-2 text-sm",
  lg: "px-6 py-3 text-base",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { variant = "primary", size = "md", loading, disabled, className = "", children, ...rest },
    ref
  ) => {
    return (
      <button
        ref={ref}
        disabled={disabled || loading}
        className={[
          "inline-flex items-center justify-center gap-2 rounded-md font-medium transition-colors disabled:cursor-not-allowed",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
          variantClasses[variant],
          sizeClasses[size],
          className,
        ].join(" ")}
        {...rest}
      >
        {loading && (
          <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
          </svg>
        )}
        {children}
      </button>
    );
  }
);
Button.displayName = "Button";
